import { FC, useEffect, useState, useRef } from "react";
import { InspectionLog } from "../../types/InspectionLog";
import {
  LabelText,
  StyledCompleteLeakTestJob,
} from "./CompleteLeakTestJob.styles";
import { Col, Row } from "react-grid-system";
import { DropDownListComponent, Inject } from "@syncfusion/ej2-react-dropdowns";
import { InspectionToolSet } from "../../types/IInspectionToolSet";
import {
  getDocuments,
  getInspectionToolSets,
  getJobInspectionLogs,
  getLeakTestCertificatesForProductionOrder,
  getOption,
  saveLeakTestCertificate,
} from "../../services/ApiService";
import { ToastUtility } from "@syncfusion/ej2-react-notifications";
import { useAuth } from "../../utility/useAuth";
import { InspectionStatus } from "../../types/InspectionStatus";
import { IJob } from "../../types/IJob";
import {
  NumericTextBoxComponent,
  TextBoxComponent,
} from "@syncfusion/ej2-react-inputs";
import {
  ColumnDirective,
  ColumnsDirective,
  ContextMenu,
  ContextMenuItemModel,
  GridComponent,
  QueryCellInfoEventArgs,
  Resize,
  RowSelectEventArgs,
  SelectionSettingsModel,
} from "@syncfusion/ej2-react-grids";
import { LeakTestCertificate } from "../../types/LeakTestCertificate";
import {
  ButtonComponent
} from "@syncfusion/ej2-react-buttons";
import { IUserInfo } from "../../types/IUserInfo";
import { TestType } from "../../types/TestType";
import { shortDateFormat } from "../../utility/dateHelpers";
import { getPathItem } from "../../utility/pathHelper";
import { MenuEventArgs } from "@syncfusion/ej2-react-navigations";
import { getUniquePartProductionOrderNumber } from "../../utility/partOrderHelper";
import { TooltipComponent } from "@syncfusion/ej2-react-popups";
import {
  printCertificate,
  savePdf,
  updateDefaultValues,
  validateTest,
} from "../../utility/testHelper";

interface ICompleteJob {
  job: IJob | undefined;
  operation: string;
  user: IUserInfo;
  onValidate: (
    inspectionToolSet: InspectionToolSet | undefined,
    reason: string | undefined,
    operationStatus: string | undefined,
    inspectionLogs: InspectionLog[] | undefined,
  ) => void;
}

export const CompleteLeakTestJob: FC<ICompleteJob> = ({
  job,
  operation,
  user,
  onValidate,
}) => {
  const [inspectionToolSet, setInspectionToolSet] =
    useState<InspectionToolSet>();
  const [inspectionToolSets, setInspectionToolSets] =
    useState<InspectionToolSet[]>();
  const [inspectionLogs, setInspectionLogs] = useState<InspectionLog[]>();
  const [certificates, setCertificates] = useState<LeakTestCertificate[]>();
  const [certificate, setCertificate] = useState<LeakTestCertificate>();
  const [selectedCertificateIndex, setSelectedCertificateIndex] = useState<
    number | undefined
  >(undefined);
  const [allowedLeakThreeItems, setAllowedLeakThreeItems] = useState<number[]>(
    [],
  );

  const [certificateExp, setCertificateExp] = useState<number | null>();
  const [errors, setErrors] = useState<string>();
  const [disableToolSets, setDisableToolSets] = useState<boolean>(false);
  const [operationState, setOperationState] = useState<string>();
  const [printLocations, setPrintLocations] = useState<string[]>([]);
  const [selectedPrinterLocation, setSelectedPrinterLocation] =
    useState<string>();
  const [printerName, setPrintName] = useState<string>();

  const auth = useAuth();

  useEffect(() => {
    if (operation && job?.jobNumber) {
      const fetchData = async () => {
        // get the data from the api
        const toolSets = await getInspectionToolSets(auth, operation);
        setInspectionToolSets(toolSets);
        const rejectedInspectionLogs = await getJobInspectionLogs(
          auth,
          job.jobNumber,
          InspectionStatus.Rejected,
        );
        populateInspectionLogs(rejectedInspectionLogs, job);

        const certificates = (
          await getLeakTestCertificatesForProductionOrder(auth, job.jobNumber)
        ).filter(
          (c) =>
            c.testType === TestType[TestType.LeakTestInboard] ||
            c.testType === TestType[TestType.LeakTestOutboard],
        );

        //const printLocations = await getOptions(auth, "PrintLocation");
        const documents = await getDocuments(
          auth,
          undefined,
          "print-locations",
          true,
        );
        const locations = documents.map(
          (document) => getPathItem(document).fileName,
        );
        const defaultPrinter = await getOption(
          auth,
          `DocumentPrinter:${user.printLocation}`,
        );

        setPrintLocations(locations);
        setSelectedPrinterLocation(user.printLocation);
        setPrintName(defaultPrinter);

        populateCertificates(certificates, toolSets, job);
      };

      fetchData()
        // make sure to catch any error
        .catch((error) => {
          const message = `Failed to get data. Error: ${error}`;
          console.error(message);
          ToastUtility.show(message, "Error", 2000);
        });
    }
  }, [operation]);

  function populateInspectionLogs(inspectionLogs: InspectionLog[], job: IJob) {
    if (inspectionLogs && inspectionLogs.length > 0) {
      const logs = inspectionLogs.map((log) => {
        log.rework = true;
        return log;
      });
      setInspectionLogs(logs);
    } else {
      let logs: InspectionLog[] = [];
      for (let i = 1; i <= job.qtyOrdered; i++) {
        logs.push({
          id: undefined,
          job: job.jobNumber,
          operation: operation,
          line: i,
          associate: user.code,
          weldId: `${job.jobNumber}-${i}`,
          dateCreated: new Date(),
          status: "",
          isAccepted: true,
          comment: "",
          rework: false,
          note: undefined,
          rejectionReason: undefined,
        });
        setInspectionLogs(logs);
      }
    }
  }

  function populateCertificates(
    leakTestCerts: LeakTestCertificate[] | undefined,
    toolSets: InspectionToolSet[],
    job: IJob,
  ) {
    const selectedStation = leakTestCerts?.find((c) => c.id > 0)?.station;
    const selectedToolSetId = leakTestCerts?.find(
      (c) => c.id > 0,
    )?.inspectionToolSetId;

    if (leakTestCerts && leakTestCerts.length) {
      const toolSet = toolSets.find((tool) => tool.name === selectedStation);
      setInspectionToolSet(toolSet);
      setDisableToolSets(true);
    }

    let certificates: LeakTestCertificate[] = [];
    for (let i = 1; i <= job.qtyOrdered; i++) {
      const partProductionOrderNumber = `${job.jobNumber}-${i}`;
      const certificate = leakTestCerts?.find(
        (c) => c.partProductionOrderNumber === partProductionOrderNumber,
      );
      if (certificate) {
        validate(certificate);
        certificates.push(certificate);
      } else {
        certificates.push({
          id: 0,
          productionOrderNumber: job.jobNumber,
          partProductionOrderNumber: partProductionOrderNumber,
          issue: job.description,
          testType: TestType[TestType.LeakTestInboard],
          leakTwo: 10,
          date: new Date(),
          customer: job.customerName,
          partNumber: job.stockCode,
          testDuration: 5,
          testedBy: user.name,
          certificateNumber: undefined,
          leakRate: 0,
          exp: 0,
          testGas: "Helium",
          bubbleTestType: "N/A",
          testPressure: 0,
          serialNumber: "",
          passed: undefined,
          station: selectedStation ?? "",
          reportParameters: "",
          customerPurchaseOrderNumber: job.customerPONumber,
          acceptableRate: 5,
          acceptableExp: -9,
          status: "",
          inspectionToolSetId: selectedToolSetId,
          springKit: "",
          temperature: 0,
        });
      }
    }
    setCertificates(certificates);
  }

  useEffect(() => {
    console.log(
      `onValidate ${JSON.stringify(inspectionToolSet)} ${JSON.stringify(inspectionLogs)}`,
    );
    let operationStatus: string | undefined;
    if (certificates) {
      if (certificates.some((certificate) => certificate.passed === false)) {
        operationStatus = InspectionStatus[InspectionStatus.Rejected];
      }

      if (certificates.every((certificate) => certificate.passed === true)) {
        operationStatus = InspectionStatus[InspectionStatus.Accepted];
      }
    }

    const logs = inspectionLogs?.map((log) => {
      const cert = certificates?.find(
        (certificate) =>
          certificate.id > 0 &&
          certificate.partProductionOrderNumber === log.weldId &&
          (certificate.passed === true || certificate.passed === false),
      );
      if (cert) {
        log.status = cert.passed
          ? InspectionStatus[InspectionStatus.Accepted]
          : InspectionStatus[InspectionStatus.Rejected];
      }
    });

    setOperationState(operationStatus);
    onValidate(inspectionToolSet, "", operationStatus, inspectionLogs);
  }, [inspectionToolSet, inspectionLogs, certificates]);

  function updateCertificates(certificate: LeakTestCertificate) {
    if (certificate) {
      certificate = validate(certificate);
      setCertificate(certificate);

      if (certificates) {
        const index = certificates.findIndex(
          (c) =>
            c.partProductionOrderNumber ===
            certificate.partProductionOrderNumber,
        );

        if (index >= 0) {
          let certs = [...certificates];
          certs[index] = certificate;

          setCertificates([...certs]);
          setSelectedCertificateIndex(index);
          grid.current?.refresh();
          grid.current?.selectRow(index);
        } else {
          console.warn(
            `Unable to select certificate row, index not found: ${certificate.partProductionOrderNumber}`,
          );
        }
      }
    }
  }

  function validate(certificate: LeakTestCertificate): LeakTestCertificate {
    if (certificate) {
      const error = validateTest(certificate);
      setErrors(error);
    }
    return certificate;
  }

  function inspectionToolSetTemplate(data: any): JSX.Element {
    return (
      <Row align="stretch">
        <Col>
          <span>{data.name}</span>
        </Col>
        <Col>
          <div
            style={{
              borderStyle: "solid",
              backgroundColor: data.colour.replace("#FF", "#").toLowerCase(),
              minWidth: 100,
              height: 20,
            }}
          ></div>
        </Col>
      </Row>
    );
  }

  function onToolSetChange(args: any) {
    if (args) {
      const toolSet = inspectionToolSets?.find(
        (tool) => tool.name === args.value,
      );
      setInspectionToolSet(toolSet);
      //setInspectionToolSet(args.value);
      if (certificates) {
        const certs = certificates.map((c) => {
          c.station = args.value;
          c.inspectionToolSetId = toolSet?.id;
          return c;
        });
        setCertificates(certs);
      }

      if (certificate) {
        certificate.station = args.value;
        setCertificate(certificate);
        validate(certificate);
      }
    }
  }

  function onTestSelected(args: RowSelectEventArgs) {
    console.log(`onTestSelected: ${args.rowIndex}`);
    if (args.rowIndex !== undefined && certificates) {
      //const selectedCertificate = certificates[args.rowIndex];
      updateDefaultValues(certificates[args.rowIndex], certificates);
      validate(certificates[args.rowIndex]);
      setCertificate(certificates[args.rowIndex]);

      initAllowedLeakThreeItems(
        certificates[args.rowIndex].testType,
        certificates[args.rowIndex].exp,
      );
    }
  }

  function onTestTypeChange(args: any) {
    if (args && certificate) {
      certificate.testType = args.value;
      certificate.acceptableExp =
        certificate.testType === TestType[TestType.LeakTestInboard] ? -9 : -4;
      initAllowedLeakThreeItems(args.value, certificate.exp);
      setCertificate(certificate);
      updateCertificates(certificate);
    }
  }

  function onTestDurationChange(args: any) {
    if (args && certificate) {
      certificate.testDuration = args.value;
      setCertificate(certificate);
      updateCertificates(certificate);
    }
  }

  function onAcceptableRateChange(args: any) {
    if (args && certificate) {
      certificate.acceptableRate = args.value;
      setCertificate(certificate);
      updateCertificates(certificate);
    }
  }

  function initAllowedLeakThreeItems(testType: string, certExp: number) {
    let allowedItems: number[] = [];
    if (testType === TestType[TestType.LeakTestInboard]) {
      allowedItems.push(-9);
      allowedItems.push(-10);
      allowedItems.push(-11);
    } else {
      allowedItems.push(-6);
      allowedItems.push(-5);
      allowedItems.push(-4);
    }
    setAllowedLeakThreeItems(allowedItems);
    const exp = allowedItems.some((i) => {
      return i === certExp;
    })
      ? certExp
      : null;
    console.log(`cert exp: ${exp}`);
    setCertificateExp(exp);
  }

  function onLocationChange(args: any) {
    if (args) {
      setSelectedPrinterLocation(args.value);
      getOption(auth, `DocumentPrinter:${args.value}`).then((option) => {
        setPrintName(option);
      });
    }
  }

  function saveCertificate(certificate: LeakTestCertificate | undefined) {
    if (certificate) {
      saveLeakTestCertificate(auth, certificate)
        .then((result) => {
          setCertificate(result);
          updateCertificates(result);
        })
        .catch((error) => {
          const message = `Failed to save the leak test certificate. Error: ${error}`;
          console.error(message);
          ToastUtility.show(message, "Error", 2000);
        });
    }
  }

  const selectionSettings: SelectionSettingsModel = {
    mode: "Row",
    type: "Single",
    persistSelection: true,
  };

  const testTypes: { [key: string]: Object }[] = [
    { value: "LeakTestInboard", displayName: "Inboard" },
    { value: "LeakTestOutboard", displayName: "Outboard" },
  ];

  const grid = useRef<GridComponent | null>(null);
  const contextMenuItems: ContextMenuItemModel[] = [
    { text: "Duplicate", target: ".e-content", id: "duplicate" },
  ];

  function duplicateTest(selectedTest: LeakTestCertificate) {
    if (!job) {
      return;
    }

    const savedStation = certificates?.find((c) => c.id > 0)?.station;
    const savedToolSetId = certificates?.find(
      (c) => c.id > 0,
    )?.inspectionToolSetId;

    const sorted = getUniquePartProductionOrderNumber(
      selectedTest.partProductionOrderNumber,
      certificates?.map((c) => c.partProductionOrderNumber) ?? [],
    );

    const weldId = sorted[0];
    const index = sorted[1];

    const cert: LeakTestCertificate = {
      id: 0,
      productionOrderNumber: selectedTest.productionOrderNumber,
      partProductionOrderNumber: weldId,
      issue: job.description,
      testType: TestType[TestType.LeakTestInboard],
      leakTwo: 10,
      date: new Date(),
      customer: job.customerName,
      partNumber: job.stockCode,
      testDuration: 5,
      testedBy: user.name,
      certificateNumber: undefined,
      leakRate: 0,
      exp: 0,
      testGas: "Helium",
      bubbleTestType: "N/A",
      testPressure: 0,
      serialNumber: "",
      passed: undefined,
      station: savedStation ?? "",
      reportParameters: "",
      customerPurchaseOrderNumber: job.customerPONumber,
      acceptableRate: 5,
      acceptableExp: -9,
      status: "",
      inspectionToolSetId: savedToolSetId,
      springKit: "",
      temperature: 0,
    };

    if (inspectionLogs && certificates) {
      setCertificates([
        ...certificates.slice(0, index),
        cert,
        ...certificates.slice(index),
      ]);
    }
  }

  const contextMenuClick = (args: MenuEventArgs) => {
    if (grid.current && args.item.id === "duplicate") {
      const selectedTest = (
        grid.current.getSelectedRecords() as LeakTestCertificate[]
      )[0];

      duplicateTest(selectedTest);
    }
  };

  const getCertificate = (
    partProductionOrderNumber: string,
  ): LeakTestCertificate | undefined => {
    return certificates?.find(
      (c) => c.partProductionOrderNumber === partProductionOrderNumber,
    );
  };

  const customizeTestCell = (args: QueryCellInfoEventArgs) => {
    if (args.cell && args.data) {
      const component = args.data as LeakTestCertificate;
      const cert = getCertificate(component.partProductionOrderNumber);

      if (cert?.status === "Failed") {
        args.cell.classList.add("test-bad");
      } else if (cert?.status === "Passed") {
        args.cell.classList.add("test-ok");
      }
    }
  };

  const canSaveCert = (partProductionOrderNumber: string): boolean => {
    const cert = certificates?.find(
      (c) => c.partProductionOrderNumber === partProductionOrderNumber,
    );
    return (
      (cert &&
        validateTest(cert)?.length === 0 &&
        (cert.id < 1 || cert?.passed !== true)) ??
      false
    );
  };

  const testBtnsTemplate = (props: LeakTestCertificate) => {
    return (
      <div>
        <TooltipComponent content="Duplicate test">
          <ButtonComponent
            iconCss="e-icons e-copy"
            onClick={() => {
              duplicateTest(props);
            }}
          />
        </TooltipComponent>
        <TooltipComponent content="Save test">
          <ButtonComponent
            disabled={!canSaveCert(props.partProductionOrderNumber)}
            iconCss="e-icons e-save"
            onClick={() => {
              saveCertificate(getCertificate(props.partProductionOrderNumber));
            }}
          />
        </TooltipComponent>
        <TooltipComponent content="Print test">
          <ButtonComponent
            disabled={!props.id || !printerName || !selectedPrinterLocation}
            iconCss="e-icons e-print"
            onClick={() => {
              printCertificate(
                auth,
                getCertificate(props.partProductionOrderNumber),
                printerName!,
                selectedPrinterLocation!,
              );
            }}
          />
        </TooltipComponent>
        <TooltipComponent content="Save as PDF">
          <ButtonComponent
            disabled={!props.id}
            iconCss="e-icons e-export-pdf"
            onClick={() => {
              savePdf(auth, getCertificate(props.partProductionOrderNumber));
            }}
          />
        </TooltipComponent>
      </div>
    );
  };

  return (
    <StyledCompleteLeakTestJob>
      <Row align="stretch">
        <Col sm={4}>
          <LabelText>Select inspection tool set:</LabelText>
        </Col>
        <Col>
          <DropDownListComponent
            enabled={!disableToolSets}
            dataSource={inspectionToolSets}
            fields={{ text: "name", value: "name" }}
            valueTemplate={inspectionToolSetTemplate}
            itemTemplate={inspectionToolSetTemplate}
            value={inspectionToolSet?.name}
            onChange={onToolSetChange}
          />
        </Col>
      </Row>
      <Row align="stretch">
        <Col sm={4}>
          <LabelText>Customer:</LabelText>
        </Col>
        <Col>
          <TextBoxComponent value={job?.customerName} readOnly />
        </Col>
      </Row>
      <Row align="stretch">
        <Col sm={4}>
          <LabelText>Part Number:</LabelText>
        </Col>
        <Col>
          <TextBoxComponent value={job?.stockCode} readOnly />
        </Col>
      </Row>
      <Row align="stretch">
        <Col sm={6}>
          <GridComponent
            id="Grid"
            ref={(g: GridComponent) => (grid.current = g)}
            dataSource={certificates}
            selectionSettings={selectionSettings}
            selectedRowIndex={selectedCertificateIndex}
            allowPaging={false}
            contextMenuItems={contextMenuItems}
            contextMenuClick={contextMenuClick}
            allowResizing
            height={320}
            gridLines="Both"
            allowSelection={true}
            rowSelected={onTestSelected}
            queryCellInfo={customizeTestCell}
          >
            <Inject services={[Resize, ContextMenu]} />
            <ColumnsDirective>
              <ColumnDirective
                field="partProductionOrderNumber"
                isPrimaryKey={true}
                headerText="Part Production Order"
                width="150"
                textAlign="Left"
              />
              <ColumnDirective
                field="status"
                isPrimaryKey={true}
                headerText="Test Result"
                width="100"
                textAlign="Left"
              />
              <ColumnDirective
                isPrimaryKey={true}
                headerText=""
                width="50"
                textAlign="Left"
                template={testBtnsTemplate}
              />
            </ColumnsDirective>
          </GridComponent>
        </Col>
        {certificate && (
          <Col>
            <Row align="stretch">
              <Col sm={6}>
                <LabelText>Certificate Number</LabelText>
              </Col>
              <Col>
                <TextBoxComponent
                  value={
                    certificate?.certificateNumber
                      ?.toString()
                      ?.padStart(6, "0") ?? ""
                  }
                  readOnly
                />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={6}>
                <LabelText>Date of Test</LabelText>
              </Col>
              <Col>
                <TextBoxComponent
                  value={shortDateFormat(certificate?.date?.toString())}
                  readOnly
                />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={6}>
                <LabelText>Leak Test Type</LabelText>
              </Col>
              <Col>
                <DropDownListComponent
                  value={certificate?.testType}
                  fields={{ text: "displayName", value: "value" }}
                  dataSource={testTypes}
                  onChange={onTestTypeChange}
                />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={6}>
                <LabelText>Duration (minutes)</LabelText>
              </Col>
              <Col>
                <NumericTextBoxComponent
                  value={certificate?.testDuration}
                  change={onTestDurationChange}
                />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={3}>
                <LabelText>Acceptable Leak Rate</LabelText>
              </Col>
              <Col>
                <NumericTextBoxComponent
                  min={0}
                  value={certificate?.acceptableRate}
                  change={onAcceptableRateChange}
                />
              </Col>
              <Col sm={1}>
                <LabelText>x10</LabelText>
              </Col>
              <Col>
                <NumericTextBoxComponent
                  value={certificate?.acceptableExp}
                  change={(args: any) => {
                    if (args && certificate) {
                      certificate.acceptableExp = args.value;
                      setCertificate(certificate);
                      updateCertificates(certificate);
                    }
                  }}
                />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={3}>
                <LabelText>Actual Leak Rate</LabelText>
              </Col>
              <Col>
                <NumericTextBoxComponent
                  min={0}
                  value={certificate?.leakRate}
                  change={(args: any) => {
                    if (args && certificate) {
                      certificate.leakRate = args.value;
                      setCertificate(certificate);
                      updateCertificates(certificate);
                    }
                  }}
                />
              </Col>
              <Col sm={1}>
                <LabelText>x10</LabelText>
              </Col>
              <Col>
                <DropDownListComponent
                  value={certificateExp}
                  dataSource={allowedLeakThreeItems}
                  onChange={(args: any) => {
                    if (args && certificate) {
                      certificate.exp = args.value;
                      setCertificateExp(args.value);
                      setCertificate(certificate);
                      updateCertificates(certificate);
                    }
                  }}
                />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={6}>
                <LabelText>Test Result</LabelText>
              </Col>
              <Col>
                <TextBoxComponent
                  value={
                    certificate?.passed === true
                      ? "Passed"
                      : certificate?.passed === false
                        ? "Failed"
                        : ""
                  }
                  readOnly
                />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={6}>
                <LabelText>Tested By</LabelText>
              </Col>
              <Col>
                <TextBoxComponent value={certificate?.testedBy} readOnly />
              </Col>
            </Row>
            <Row align="stretch">
              <Col sm={6}>
                <LabelText>Print Location</LabelText>
              </Col>
              <Col>
                <DropDownListComponent
                  id="printLocElement"
                  dataSource={printLocations}
                  value={selectedPrinterLocation}
                  placeholder="Select a printer location"
                  onChange={onLocationChange}
                />
              </Col>
            </Row>
            <Row align="stretch">
              <h6 style={{ color: "red" }}>{errors}</h6>
            </Row>
          </Col>
        )}
      </Row>
      <Row align="stretch">
        <Col sm={4}>
          <LabelText>Operation Result:</LabelText>
        </Col>
        <Col>
          <TextBoxComponent value={operationState} readOnly />
        </Col>
      </Row>
    </StyledCompleteLeakTestJob>
  );
};
